import {useHref} from '@sail/navigation';
import Head from 'next/head';
import {useCallback, useMemo} from 'react';
import {
  AlternateLanguageDefaults,
  DEFAULT_PREFIX,
  PathPrefixes,
  PrefixablePair,
  getPrefixForPair,
  useGlobalizationPrefixData,
} from 'mkt-ssr/lib/globalization';

export function GlobalizedAlternates() {
  const [href] = useHref();
  const prefixData = useGlobalizationPrefixData();
  const unglobalizedHref = useMemo(
    () => href.replace(new RegExp(`^/${prefixData.prefix}`), ''),
    [href, prefixData.prefix],
  );

  // NB: This is very much not the way we want to typically
  // do this, however for this particular case the @sail/navigation
  // tooling does not meet our needs, so we manually construct the URLs.
  const createHrefForPair = useCallback(
    (pair: PrefixablePair) => {
      const prefix = getPrefixForPair(pair);
      const url = new URL(
        prefix === DEFAULT_PREFIX.prefix
          ? unglobalizedHref
          : `/${prefix}${unglobalizedHref}`,
        'https://stripe.com',
      );
      return url.toString();
    },
    [unglobalizedHref],
  );

  return (
    <Head>
      <link rel="canonical" href={createHrefForPair(prefixData)} />
      <link
        rel="alternate"
        href={createHrefForPair(DEFAULT_PREFIX)}
        hrefLang="x-default"
      />
      {Object.values(PathPrefixes).map((prefix) => (
        <link
          rel="alternate"
          href={createHrefForPair(prefix)}
          hrefLang={prefix.hreflang}
          key={`prefix-${prefix.prefix}`}
        />
      ))}
      {AlternateLanguageDefaults.map((pair) => (
        <link
          rel="alternate"
          href={createHrefForPair(pair)}
          hrefLang={pair.locale.iso6391}
          key={`default-${pair.locale.iso6391}`}
        />
      ))}
    </Head>
  );
}
